import { Helmet } from "react-helmet-async"

interface SEOProps {
  title: string
  description: string
  keywords?: string
  canonicalUrl?: string
  ogImage?: string
  ogType?: "website" | "article" | "product" | "profile"
  twitterCard?: "summary" | "summary_large_image"
  jsonLd?: Record<string, any> | Record<string, any>[]
}

export function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "https://rent-keep.com/app-icon.png",
  ogType = "website",
  twitterCard = "summary_large_image",
  jsonLd,
}: SEOProps) {
  const defaultTitle = "RentKeep — Property Management"
  // Only append the site suffix when the page title doesn't already carry the
  // brand, otherwise pages like the landing page end up double-branded and blow
  // past the ~60 chars Google will actually show.
  const fullTitle = title.includes("RentKeep") ? title : `${title} | ${defaultTitle}`
  const url = canonicalUrl || "https://rent-keep.com"

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Open Graph (Facebook, LinkedIn, iMessage) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="RentKeep" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) for GEO/AI Search */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(jsonLd)
              ? jsonLd.map(item => ({ "@context": "https://schema.org", ...item }))
              : { "@context": "https://schema.org", ...jsonLd }
          )}
        </script>
      )}
    </Helmet>
  )
}
